import { AxiosInstance } from 'axios';
import VueRouter from 'vue-router';

import {
    JWTLoginSessionManager,
    AxiosSessionInjector,
    VueRouteTransitionGuardService,
    VueRouterRedirectionService,
    AxiosAccessDeniedErrorHandler,
} from '../services';

import { Route, NavigationGuardNext } from 'vue-router';
import { MainInjector } from '@/di/injector';
import { AuthenticationServiceTokens } from '@/authentication/di-tokens';

export function enableInfiniteAuthMiddleware(httpClient: AxiosInstance, router: VueRouter, injector: MainInjector) {
    const platformService = injector.resolve(AuthenticationServiceTokens.PlatformResolutionService);
    const sessionManager = new JWTLoginSessionManager();
    const sessionInjector = new AxiosSessionInjector(httpClient, sessionManager);
    const redirectService = new VueRouterRedirectionService(router);
    const accessDeniedErrorHandler = new AxiosAccessDeniedErrorHandler(httpClient);

    sessionInjector.install();

    /**
     * For each of the application pages that requires authorization
     * we want to check if a user is allowed to visit it.
     */
    router.beforeEach((to, _from, next) => {
        checkIfNavigationAllowed(to, next);
    });

    /**
     * Enable automatic session refresher.
     * If it can't refresh a session it will call a dedicated error handler callback.
     *
     * And it also works as an active session validator
     */
    sessionManager.setSessionRefreshErrorHandler(() => {
        // We may appear somewhere in route transition like
        // / → /app/courses
        // or /app/courses → /app/login
        // or whatever...
        // so current route may be updated later and we have to ensure that no transition will happen anymore...
        router.onReady(() => {
            if (redirectService.isAuthRequiredForCurrentRoute()) {
                platformService.forceSsoAutomaticLoginEnabled(false);
                redirectService.redirectToLoginPage(true);
            }
        });
    });
    sessionManager.resumeAutoRefresh();

    accessDeniedErrorHandler.install(() => {
        // It is mandatory as we changed user deletion and deactivation process (e.g. kong jwt and/or consumers removal, session revoking)
        sessionManager.forceSessionRefresh();
        return true;
    });
}

/**
 * For each of the application pages that requires authorization
 * we want to check if a user is allowed to visit it.
 */
function checkIfNavigationAllowed(routeTo: Route, goNext: NavigationGuardNext) {
    const sessionManager = new JWTLoginSessionManager();
    const guardService = new VueRouteTransitionGuardService(routeTo, goNext);

    if (guardService.isAuthRequiredForTargetRoute() && !sessionManager.isLoggedIn()) {
        guardService.changeTransitionToLoginPage();
        return;
    }

    if (guardService.isTargetRouteForUnauthorizedOnly() && sessionManager.isLoggedIn()) {
        guardService.changeTransitionToDefaultPage();
        return;
    }

    guardService.acceptCurrentTransition();
}
